<template>
  <div class="bg-white w-full h-full">
    <div
      class="sm:pb-12 sm:pt-5 sm:mt-16 pb-20 pt-0 sm:px-6 max-w-7xl mx-auto bg-white"
    >
      <div class="w-full">
        <ArtworkSkeleton v-if="loading" />
        <div v-else-if="artwork">
          <ArtworkPreviewModal />
          <BaseShareModal
            :url="getPageFullUrl(artwork._id)"
            :description="artwork.title[locale as Locale]"
            :media="artwork.thumbnail"
          />
          <div class="md:flex 2xl:space-x-8 md:space-x-5">
            <div class="md:w-1/2 lg:w-[60%] w-full sm:flex justify-center">
              <UCarousel
                class="w-full"
                v-slot="{ item, index }"
                loop
                :arrows="false"
                :dots="galleryItems.length > 1"
                wheel-gestures
                :prev="{ variant: 'solid' }"
                :next="{ variant: 'solid' }"
                :items="galleryItems"
                :ui="{
                  item: 'basis-full min-w-0',
                  // 1. Show only one item at a time
                  container: 'items-center',
                  controls:
                    'absolute bottom-5 sm:left-14 sm:right-14 right-2.5 left-2.5 flex items-center',
                  // 3. Make the arrows visible (adding background and positioning)

                  // 4. Style the pagination dots
                  dots: 'absolute -bottom-2.5 flex justify-center gap-x-3',
                  dot: 'size-3 min-w-3 rounded-full bg-gray-200 data-[state=active]:border-blue-600 data-[state=active]:bg-blue-600 transition-all',
                }"
              >
                <div class="relative w-full">
                  <div class="absolute sm:top-3 top-2 sm:right-3 right-2 z-10">
                    <div class="flex items-center sm:space-x-3 space-x-2.5">
                      <BaseShareButton />
                      <ArtworkLikeButton :artwork="artwork" />
                    </div>
                  </div>
                  <BaseImage
                    @click="actions.openPreview(artwork, index)"
                    :uri="item"
                    :alt="artwork.title[locale as Locale] || artwork.title.en"
                    img-class="w-full cursor-zoom-in h-auto object-contain  sm:max-h-[90vh] block mx-auto transition-opacity duration-700"
                    loader-class="w-full min-h-[60vh] animate-pulse"
                    icon-class="lg:h-16 h-12"
                    @contextmenu.prevent
                    @dragstart.prevent
                  />
                </div>
              </UCarousel>
            </div>

            <div
              class="md:w-1/2 lg:w-[40%] w-full sm:px-0 px-4 2xl:space-y-5 md:space-y-3.5 space-y-2.5 md:mt-0 mt-2.5"
            >
              <div>
                <h1
                  class="lg:text-3xl md:text-2xl text-xl font-semibold mb-0.5 wrap-break-words"
                >
                  {{ getDispayName }}
                </h1>
                <p class="italic text-gray-600 md:text-sm text-xs">
                  <span>{{ getArtTypeLocale(artwork.artType) }}</span>
                  <span v-if="artwork.creationYear">,</span>
                  <span v-if="artwork.creationYear">{{
                    artwork.creationYear
                  }}</span>
                </p>
              </div>
              <div class="flex items-center space-x-5">
                <div class="flex items-center sm:gap-x-3 gap-x-2">
                  <NuxtLink
                    :to="$localePath(`/artist/${artwork.userId._id}`)"
                    v-if="artwork.userId.avatar"
                  >
                    <BaseImage
                      @contextmenu.prevent
                      @dragstart.prevent
                      :uri="artwork.userId.avatar"
                      img-class="sm:w-11 sm:h-11 h-10 w-10 rounded-full object-cover border border-gray-200"
                      :alt="
                        `${artwork.userId.name || ''} ${artwork.userId.last_name || ''}` ||
                        'Medium.am user'
                      "
                    />
                  </NuxtLink>
                  <NuxtLink
                    :to="$localePath(`/artist/${artwork.userId._id}`)"
                    v-else
                  >
                    <BaseImage
                      @contextmenu.prevent
                      @dragstart.prevent
                      img-class="sm:w-11 sm:h-11 h-10 w-10 rounded-full object-cover border border-gray-200"
                      uri="/no_avatar.jpg"
                      alt="Placeholder avatar"
                    />
                  </NuxtLink>
                  <div>
                    <span class="sm:text-sm text-xs text-gray-600">
                      {{ $t("artwork_details.creator") }}
                    </span>
                    <h3 class="sm:text-base text-sm font-medium -mt-1">
                      {{ artwork.userId.name }} {{ artwork.userId.last_name }}
                    </h3>
                  </div>
                </div>
                <ArtworkContactArtist
                  v-if="isContempoarary"
                  :artwork="artwork"
                />
              </div>
              <div
                class="2xl:space-y-5 md:space-y-3.5 space-y-2.5 2xl:py-5 md:py-3.5 py-2.5"
              >
                <ArtworkMetadataItem
                  category="mediums"
                  :label="$t('artwork_details.medium')"
                  :items="artwork.mediums"
                  @metadata-filter="handleCategoryRedirect"
                />

                <ArtworkMetadataItem
                  category="styles"
                  :label="$t('artwork_details.styles')"
                  :items="artwork.styles"
                  @metadata-filter="handleCategoryRedirect"
                />

                <ArtworkMetadataItem
                  category="genres"
                  :label="$t('artwork_details.genres')"
                  :items="artwork.genres"
                  @metadata-filter="handleCategoryRedirect"
                />
              </div>
              <ArtworkDimensions :dimensions="artwork.dimensions" />
              <ArtworkLocation :location="artwork.location" />
              <ArtworkPrice
                :is-for-sale="artwork.isForSale"
                :price="artwork.price"
              />
              <div v-if="getDescription">
                <h4 class="text-base font-medium block mb-3">
                  {{ $t("artwork_details.about") }}
                </h4>
                <BaseDetails
                  :content="getDescription"
                  :threshold="500"
                  :empty-text="$t('artwork_details.no_description')"
                  content-class="sm:text-sm text-[13px]"
                />
              </div>
              <ArtworkColorPalette
                :color-palette="artwork.colorPalette"
                v-if="artwork.colorPalette?.length"
              />
            </div>
          </div>
        </div>

        <div v-else class="py-20 text-center">
          <BaseNotFound
            :action="true"
            :label="$t('not_found.artwork.label')"
            :title="$t('not_found.artwork.title')"
            @click="backToGallery"
          >
            <template #icon>
              <Icon name="tabler:filters-filled" size="30" />
            </template>
          </BaseNotFound>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ArtType, Artwork } from "~~/types/artwork";
import type { Locale } from "~~/types/global";
import { useI18n } from "vue-i18n";
import { artTypes } from "~~/types/artwork";
import type { MetadataType } from "~/components/artwork/metadata-item.vue";

const favorite = useFavoriteStore();
const actions = useArtworkActionStore();
const localePath = useLocalePath();

const isContempoarary = computed<boolean>(() => {
  if (!artwork.value) return false;

  const user = artwork.value.userId;

  if (user.email?.endsWith("@arm-art.com")) {
    return false;
  }

  return (
    !!user.phone_number || Object.values(user.social_links || {}).some(Boolean)
  );
});

const getDispayName = computed<string>(() => {
  const lang = locale.value as Locale;
  return (
    artwork.value?.title[lang] ||
    artwork.value?.title.hy ||
    artwork.value?.title.ru ||
    artwork.value?.title.en ||
    ""
  );
});

const getPageFullUrl = (id: string) => {
  const base = "https://medium.am";
  const destination = `/artwork-details/${id}`;
  const fullPath = `${base}${destination}`;
  return fullPath;
};

const getDescription = computed<string>(() => {
  const lang = locale.value as Locale;
  return (
    artwork.value?.description[lang] ||
    artwork.value?.description.en ||
    artwork.value?.description.ru ||
    artwork.value?.description.hy ||
    ""
  );
});

const backToGallery = () => navigateTo(localePath("/gallery"));

const getArtTypeLocale = (art_typ: ArtType): string => {
  // 1. Find the item
  const found = artTypes.find((item) => item.key === art_typ);

  // 2. Safely access the locale with a fallback to the key itself
  // We cast locale.value as keyof typeof found.label to satisfy TS
  return found?.label[locale.value as keyof typeof found.label] || art_typ;
};

const handleCategoryRedirect = async (type: MetadataType, key: string) => {
  const destinationParams = `?${type}=${key}`;
  const destinationPath = "categories";
  const finalDestination = `/${destinationPath}${destinationParams}`;
  await navigateTo(localePath(finalDestination));
};

const { locale } = useI18n();
const route = useRoute();
const artworkId = computed<string>(() => route.params.id as string);

interface ArtworkResponse {
  data: Artwork;
  status?: string;
}

const { data, pending: loading } = useApi<ArtworkResponse>(
  `/art/artwork/${artworkId.value}`,
  {
    lazy: true,
    server: true,
  },
);

const artwork = computed<Artwork | null>(() => {
  if (!data.value || !data.value?.data) return null;
  return data.value?.data;
});

const galleryItems = computed<string[]>(() => {
  const mainImage = artwork.value?.thumbnail;
  const gallery = artwork.value?.gallery || [];

  // Create a new array to avoid mutating the original artwork.gallery
  return mainImage ? [mainImage, ...gallery] : [...gallery];
});

watch(
  () => artwork.value,
  async () => {
    if (artwork.value?._id) {
      await favorite.getFavorites();
    }
  },
  { immediate: true },
);

useSeoMeta({
  title: () => artwork.value?.title?.[locale.value as Locale] || "MEDIUM.am",
  ogTitle: () => artwork.value?.title?.[locale.value as Locale] || "MEDIUM.am",
  description: () =>
    artwork.value?.description?.[locale.value as Locale] ||
    "The heart of Armenian Art",
  ogDescription: () =>
    artwork.value?.description?.[locale.value as Locale] ||
    "The heart of Armenian Art",
  ogImage: () => artwork.value?.thumbnail || "https://medium.am/logo.png",
  twitterImage: () => artwork.value?.thumbnail || "https://medium.am/logo.png",
  ogType: "website",
  twitterCard: "summary",
});
</script>
